import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ORDER_DISCOUNT_CONDITION_MIN_SPEND,
  ORDER_DISCOUNT_CONDITION_MIN_QUANTITY,
  ORDER_DISCOUNT_TYPE_FIXED,
  ORDER_DISCOUNT_TYPE_PERCENTAGE,
} from '_utils/constant';
import './style.scss';

export default function ConfigOrderDiscounts({ hasPaidOrder }) {
  const { watch, setValue } = useFormContext() || {};
  const formOrderDiscount = watch ? watch('orderDiscount') : null;

  const [discount, setDiscount] = useState(formOrderDiscount);
  const [isOpenForm, setIsOpenForm] = useState(!formOrderDiscount);

  const [condType, setCondType] = useState(ORDER_DISCOUNT_CONDITION_MIN_SPEND);
  const [condVal, setCondVal] = useState('');
  const [discType, setDiscType] = useState(ORDER_DISCOUNT_TYPE_FIXED);
  const [discAmt, setDiscAmt] = useState('');

  const resetForm = () => {
    setCondType(ORDER_DISCOUNT_CONDITION_MIN_SPEND);
    setCondVal('');
    setDiscType(ORDER_DISCOUNT_TYPE_FIXED);
    setDiscAmt('');
  };

  const handleSave = () => {
    if (condVal === '' || discAmt === '') return;
    const discountObj = {
      condition: condType,
      threshold: Number(condVal),
      type: discType,
      amount: Number(discAmt),
    };
    setDiscount(discountObj);
    if (setValue) {
      setValue('orderDiscount', discountObj);
      setValue('orderDiscountEnabled', true);
    }
    resetForm();
    setIsOpenForm(false);
  };

  const handleCancel = () => {
    resetForm();
    setIsOpenForm(false);
  };

  const handleDelete = () => {
    setDiscount(null);
    if (setValue) {
      setValue('orderDiscount', null);
      setValue('orderDiscountEnabled', false);
    }
    resetForm();
    setIsOpenForm(true);
  };

  const handleEdit = () => {
    if (discount) {
      setCondType(discount.condition);
      setCondVal(discount.threshold);
      setDiscType(discount.type);
      setDiscAmt(discount.amount);
      setIsOpenForm(true);
    }
  };

  const getDiscountDisplayText = () => {
    if (!discount) return '';
    const hasThreshold = Number(discount.threshold) > 0;
    const conditionText = hasThreshold
      ? (discount.condition === ORDER_DISCOUNT_CONDITION_MIN_SPEND
        ? `Spend $${Number(discount.threshold).toFixed(2)}+`
        : `Order ${discount.threshold}+ items`)
      : '';
    const discountText = discount.type === ORDER_DISCOUNT_TYPE_FIXED
      ? `$${Number(discount.amount).toFixed(2)} off`
      : `${discount.amount}% off`;
    return conditionText ? `${conditionText} → ${discountText}` : discountText;
  };

  const isFormValid = condVal !== '' && Number(condVal) >= 0 && discAmt !== '' && Number(discAmt) >= 0;

  return (
    <div className="discount-config-container">
      {discount && (
        <div className="tier-list-container">
          <div className="tier-item">
            <div className="tier-info">
              <span className="tier-tag">🏷</span>
              <div className="tier-info-content">
                <span className="tier-text">{getDiscountDisplayText()}</span>
              </div>
            </div>
            <div className="tier-actions">
              {!hasPaidOrder && (
                <>
                  <button
                    type="button"
                    className="tier-action-btn btn-tier-edit"
                    onClick={handleEdit}
                    aria-label="Edit discount"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className="tier-action-btn btn-tier-delete"
                    onClick={handleDelete}
                    aria-label="Delete discount"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {isOpenForm && (
        <div className="tier-form">
          <div className="tier-form-title">NEW DISCOUNT TIER</div>
          <div className="tf-row">
            <span className="tf-lbl">Condition</span>
            <select
              className="tier-select"
              value={condType}
              onChange={(e) => setCondType(e.target.value)}
              style={{ gridColumn: 'span 2' }}
            >
              <option value={ORDER_DISCOUNT_CONDITION_MIN_SPEND}>Min. spend</option>
              <option value={ORDER_DISCOUNT_CONDITION_MIN_QUANTITY}>Min. quantity</option>
            </select>
          </div>
          <div className="tf-row">
            <span className="tf-lbl" style={{ color: '#B4B2A9' }}>over</span>
            <div className="tf-amt-wrap" style={{ gridColumn: 'span 2' }}>
              {condType === ORDER_DISCOUNT_CONDITION_MIN_SPEND && <span className="tf-pfx">$</span>}
              <input
                className="tier-amt-input"
                type="number"
                min="0"
                step={condType === ORDER_DISCOUNT_CONDITION_MIN_SPEND ? '0.50' : '1'}
                placeholder="0"
                value={condVal}
                onChange={(e) => setCondVal(e.target.value)}
              />
              {condType === ORDER_DISCOUNT_CONDITION_MIN_QUANTITY && <span className="tf-pfx" style={{ marginLeft: '2px' }}>items</span>}
            </div>
          </div>
          <div style={{ height: '0.5px', background: '#e0dfd8' }}></div>
          <div className="tf-row">
            <span className="tf-lbl">Discount</span>
            <div className="type-toggle">
              <button
                type="button"
                className={`type-pill ${discType === ORDER_DISCOUNT_TYPE_FIXED ? 'active' : ''}`}
                onClick={() => setDiscType(ORDER_DISCOUNT_TYPE_FIXED)}
              >
                $ Fixed
              </button>
              <button
                type="button"
                className={`type-pill ${discType === ORDER_DISCOUNT_TYPE_PERCENTAGE ? 'active' : ''}`}
                onClick={() => setDiscType(ORDER_DISCOUNT_TYPE_PERCENTAGE)}
              >
                %
              </button>
            </div>
            <div className="tf-amt-wrap">
              {discType === ORDER_DISCOUNT_TYPE_FIXED && <span className="tf-pfx">$</span>}
              <input
                className="tier-amt-input"
                type="number"
                min="0"
                step="0.5"
                placeholder="0"
                value={discAmt}
                onChange={(e) => setDiscAmt(e.target.value)}
              />
              {discType === ORDER_DISCOUNT_TYPE_PERCENTAGE && <span className="tf-pfx" style={{ marginLeft: '2px' }}>%</span>}
            </div>
          </div>
          <div className="tier-form-footer">
            <button type="button" className="tier-cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="tier-save-btn"
              onClick={handleSave}
              disabled={!isFormValid}
            >
              Save →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
